import { computed, config, debug, george, i18n, observable, observer, pr, React, theme, ui } from "aaa";
import { NativeFlatButton } from "components/common/NativeFlatButton";
import { NativePaper } from "components/common/NativePaper";
import { NativeRaisedButton } from "components/common/NativeRaisedButton";
import { NativeTextField } from "components/common/NativeTextField";
import { map as _map, uniq as _uniq } from "lodash";
import { history } from "stores/RouterStore";
import { IPhoneOption, ISigningInfo } from "types/signing";
import { BackButton } from "../../util/BackButton";
import { SigningStore } from "../stores/SigningStore";
import { ChoosePhoneDialog } from "./ChoosePhoneDialog";
import Flicker from "./Flicker";
import { TopBackgroundColor } from "components/util/TopBackgroundColor";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import trackingStore from "stores/TrackingStore";

interface IProps {
    signingInfo?: ISigningInfo;

    // Info message string displayed depending on the signing method
    signInfoTac?: string;
    signInfoCardTan?: string;
    signInfoSid?: string;
    signInfoGeorgeToken?: string;

    // Currently for IchApp only - polling only
    pollingUrl?: string;
    authTransactionId?: string;
    // ---

    content: any;

    onSuccess: (data: any) => void;
    onError?: (error: any) => void;
    onRetry?: () => void;

    // If no onClose() function is provided, history.goBack() is applied
    onClose?: () => void;

    // Optional isLoading if e.g. doing some API calls in onSuccess (e.g. getPin)
    isLoading?: boolean;
    // For HYB-100 design changes, activate for relevant screens
    useWebVersion?: boolean;
}

interface IState {
    tacInputFieldValue: string;
}

export const SigningComponentWrapper = observer((props: { children: any, className?: string, useWebVersion?: boolean }) => {
    const topSpacing = props.useWebVersion && !externalInterfaceStore.isGeorgeApp ? theme.WEB_HEADER_HEIGHT + theme.HEADER_CONTENT_SPACING : theme.WEB_HEADER_HEIGHT;

    return (
        <div
            className={props.className}
            style={{
                position: "absolute",
                top: externalInterfaceStore.isGeorgeWeb ? topSpacing : 0,
                left: 0,
                right: 0,
                textAlign: "center",
                minHeight: `calc(100% - ${externalInterfaceStore.isGeorgeWeb ? topSpacing : 0}px)`
            }}
        >
            {props.children}
        </div>
    );
});

export const SigningHeaderBackground = () => {
    // First div is a hack to make second div overscroll on top
    return (
        <div
            className="theme-header-background-color"
            style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: 160,
            }}
        />
    );
};

// Resend, Cancel buttons
const FlatButton = (props: { disabled: boolean, labelId: i18n.IDS, onClick: () => void, buttonStyle?: React.CSSProperties }) => {
    const buttonStyle = {
        width: "50%",
        fontWeight: "bold" as any,
        color: props.disabled === true ? "grey" : theme.themeColors.blue_300,
        ...props.buttonStyle
    };

    return (
        <NativeFlatButton
            disabled={props.disabled}
            style={buttonStyle}
            onClick={() => props.onClick()}
        >
            <i18n.FormattedMessage id={props.labelId} />
        </NativeFlatButton>
    );
};

@i18n.injectIntl
@observer
export class SigningComponent extends React.Component<IProps & i18n.InjectedIntlProps, IState> {
    @observable signingStore: SigningStore;
    @observable isRetrying = false;

    constructor(props: IProps) {
        super(props);
        this.signingStore = new SigningStore();

        debug.log("### signing constructor");

        this.state = {
            tacInputFieldValue: ""
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        this.startSigning();
    }

    /**
     * If props during ctor did not contain signingInfo then start signing with
     * newly received props
     */
    componentDidUpdate(prevProps: IProps) {
        // Early out -> no signing info
        if (!this.props.signingInfo) {
            return;
        }

        const signInfoChanged = !prevProps.signingInfo ||
            this.props.signingInfo.signId !== prevProps.signingInfo.signId ||
            this.props.signingInfo.state !== prevProps.signingInfo.state;

        // Early out -> signing info has not changed
        if (!signInfoChanged) {
            return;
        }

        // Restart if in INIT or ERROR state
        if (this.signingStore.signingState === "INIT" ||
            this.signingStore.signingState === "ERROR") {
            debug.log("### Restart signing", this.signingStore.signingState);
            this.startSigning();
        }
    }

    handleSuccess = async (data: any) => {
        await trackingStore.trackEvent({ eventName: "succeded" });
        this.props.onSuccess(data);
    }

    handleError = async (error: any) => {
        await trackingStore.trackEvent({ eventName: "failed" });
        if (this.props.onError) {
            this.props.onError(error);
        }
    }

    /**
     * Trigger signing flow
     */
    startSigning() {
        const props = this.props;

        debug.log("### startSigning", props);

        if (props.pollingUrl) {
            this.signingStore.signingMethod = "GEORGE_TOKEN";
            this.signingStore.signingState = "WAIT_FOR_AUTHORIZATION";

            this.signingStore.setPollingHandlers(this.handleSuccess, this.handleError);

            const pollingData = {
                url: props.pollingUrl,
                interval: 3000,
                id: ""
            };

            if (!props.authTransactionId) {
                this.signingStore.pollingData = pollingData;

                this.signingStore.restartGeorgeTokenPolling();
            } else {
                this.signingStore.startGeorgeTokenPolling({
                    authorizationType: "GEORGE_TOKEN",
                    signInfo: {
                        signId: "",
                        state: "OPEN"
                    },
                    authTransactionId: props.authTransactionId,
                    poll: pollingData
                });
            }

            return;
        }

        if (props.signingInfo) {
            // set signing context
            this.signingStore.signingInfo = props.signingInfo;

            // Needed for SID signing
            this.signingStore.setPollingHandlers(this.handleSuccess, this.handleError);

            // init signing
            this.signingStore.startSigning();
        }
    }

    @computed get isLoading() {
        return this.signingStore.isLoading || this.props.isLoading === true;
    }

    renderSignInfo = () => {
        if (this.isLoading) {
            return null;
        }

        let signInfo = this.props.signInfoSid || ui.__("general.signing.info.body.sid");
        switch (this.signingStore.signingMethod) {
            case "TAC":
                signInfo = this.props.signInfoTac || ui.__("general.signing.info.body.tac");
                break;
            case "CARDTAN":
                signInfo = this.props.signInfoCardTan || ui.__("general.signing.info.body.cardTan");
                break;
            case "GEORGE_TOKEN":
                signInfo = this.props.signInfoGeorgeToken || ui.__("general.signing.info.body.georgeIdentity");
                break;
            default:
                break;
        }

        return (
            <pr.P_14_CONTENT
                style={{
                    margin: "0px 20px",
                    paddingBottom: 20,
                    textAlign: "left",
                }}
            >
                {signInfo}
            </pr.P_14_CONTENT>
        );
    }

    /**
     * Render flicker code (using sample code from Flicker). Will later
     * be replaced by Flicker React component provided by sIT/beeOne
     */
    renderFlicker() {
        if (this.signingStore.signingMethod === "CARDTAN" &&
            this.signingStore.flickerParams) {
            debug.log("### flicker startCode", this.signingStore.flickerParams.startCodeValue);
            debug.log("### flicker flickerCode", this.signingStore.flickerParams.flickerCode);
            return (
                <Flicker
                    startCodeValue={this.signingStore.flickerParams.startCodeValue}
                    flickerCode={this.signingStore.flickerParams.flickerCode}
                    clockSpeed={2}
                />
            );
        } else if (config.TEST_FLICKER) {
            return <Flicker
                startCodeValue="00"
                flickerCode="05818009003D"
                clockSpeed={2}
            />;
        }
    }

    /**
     * Send TAC to backend and handle response
     */
    onSendTac = async () => {
        try {
            debug.log("###", this.state.tacInputFieldValue);
            const data = await this.signingStore.putSign(this.state.tacInputFieldValue);
            this.handleSuccess(data);
        } catch (error) {
            debug.log("### signing failed");
            this.handleError(error);
        }
    }

    handleClose = async () => {
        this.signingStore.stopPolling();
        await trackingStore.trackEvent({ eventName: "canceled" });
        this.props.onClose ? this.props.onClose() : history.goBack();
    }

    renderResendTAC() {
        return (
            <pr.HCenterDiv style={{ paddingBottom: 20 }}>
                <FlatButton buttonStyle={{ width: 240 }} disabled={this.isLoading} labelId="signing.button.resend.tac" onClick={this.signingStore.startSigning} />
            </pr.HCenterDiv>
        );
    }

    renderRetrySID() {
        return (
            <div style={{ textAlign: "center", marginTop: theme.MARGIN }}>
                <FlatButton
                    disabled={this.isLoading || this.isRetrying}
                    labelId="sid.tryAgain"
                    onClick={async () => {
                        if (this.props.onRetry) {
                            this.isRetrying = true;
                            await this.props.onRetry();
                            this.isRetrying = false;
                        } else {
                            this.signingStore.startSigning();
                        }
                    }}
                />
            </div>
        );
    }

    renderErrorMessage() {
        if (this.signingStore.hasError) {
            // Signing can deliver more than one error -> convert all of them to strings
            const errorStrings = _uniq(_map(this.signingStore.signingErrorState,
                code => {
                    const stringId = this.signingStore.errorToStringId(code);
                    return ui.__(stringId);
                }
            ));

            // Create labels from strings
            const labels = _map(errorStrings, (message, index) => (
                <pr.P
                    style={{
                        color: "#B33C3C",
                        fontSize: "14px"
                    }}
                    key={index}
                >
                    {message}
                </pr.P>)
            );

            return (
                <div
                    style={{
                        marginTop: 4,
                        padding: 8,
                        borderRadius: 4,
                        border: "solid 1px #B33C3C",
                        background: "#ffe2e2"
                    }}
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {labels}
                </div>
            );
        }
    }

    onTacValue = (event: any, value: string) => {
        const method = this.signingStore.signingMethod;
        if ((method === "TAC" && value.length <= 4) ||
            (method === "CARDTAN" && value.length <= 7)
        ) {
            this.setState({ tacInputFieldValue: value });
        }
    }

    validTacEntered = () => {
        const method = this.signingStore.signingMethod;
        const tac = this.state.tacInputFieldValue;
        return (method === "TAC" && tac.length === 4) ||
            (method === "CARDTAN" && tac.length === 7);
    }

    renderCodeEntryField() {
        const codeLabel = this.signingStore.signingMethod === "TAC" ?
            "signing.textField.label.send.tac" :
            "signing.textField.label.send.cardTan";

        const sendDisabled = !this.validTacEntered() || this.isLoading;

        const borderColor = ui.isDarkMode ? theme.themeColors.d_grey_200 : theme.themeColors.blue_200;

        return (
            <div
                style={{
                    display: "flex",
                    justifyContent: "center"
                }}
            >
                <div
                    style={{
                        // Leave out border, adding it makes it look completely different to HTML specs
                        border: `1px solid ${borderColor}`,
                        boxShadow: ui.isDarkMode ? "none" : `inset 0 0 3px 0 ${theme.themeColors.grey_200}`,
                        borderRadius: theme.BORDER_RADIUS,
                        paddingLeft: 12,
                        paddingRight: 12,
                        height: 48,
                        boxSizing: "border-box"
                    }}
                >
                    <NativeTextField
                        inputMode="decimal"
                        disabled={this.isLoading}
                        style={{ width: 85, display: "inline-block", textAlign: "center" }}
                        id="sendTac"
                        hintText={ui.__(codeLabel)}
                        aria-label={ui.__(codeLabel)}
                        onChange={this.onTacValue}
                        value={this.state.tacInputFieldValue}
                        inputStyle={{ textAlign: "center" }}
                    />
                </div>

                <NativeRaisedButton
                    className="theme-action-button-background-color"
                    disabled={sendDisabled}
                    primary
                    style={{ width: 120, height: 48, marginLeft: 24, borderRadius: theme.BORDER_RADIUS, boxShadow: "none" }}
                    buttonStyle={{ borderRadius: theme.BORDER_RADIUS, color: "white" }}
                    labelStyle={{ fontSize: 16, fontWeight: 600, textTransform: "none" }}
                    onClick={this.onSendTac}
                    label={ui.__("signing.button.send.tac")}
                />
            </div>
        );
    }

    renderOverlayBox() {
        const loading = this.isLoading;
        return (
            <NativePaper
                style={{
                    margin: "20px 10px",
                    marginTop: 40,
                    borderRadius: theme.BORDER_RADIUS,
                    textAlign: "left",
                    overflow: "hidden",
                    position: "relative",
                    zIndex: 1
                }}
            >
                {loading &&
                    <div style={{ height: 200 }}>
                        <george.LoadingIndicator isLoading={loading} height="100%" />
                    </div>
                }

                {!loading &&
                    <div>
                        {this.props.content}

                        {this.signingStore.signingMethod !== "SID" && this.signingStore.signingMethod !== "GEORGE_TOKEN" && (
                            <pr.LightSeparatorLine spacing={this.props.useWebVersion ? theme.MARGIN : undefined} />
                        )}

                        <div style={{ padding: theme.MARGIN, paddingBottom: theme.MARGIN_LARGE }}>
                            {this.renderSignInfo()}
                            {this.signingStore.hasError &&
                                (this.signingStore.signingMethod === "SID" || this.signingStore.signingMethod === "GEORGE_TOKEN") && this.renderRetrySID()}
                            {this.signingStore.signingMethod !== "SID" && this.signingStore.signingMethod !== "GEORGE_TOKEN" && this.renderCodeEntryField()}
                            {this.renderErrorMessage()}
                        </div>
                    </div>
                }

            </NativePaper>
        );
    }

    render() {
        return (
            <SigningComponentWrapper useWebVersion={this.props.useWebVersion} className="theme-signing-background-color">
                {(!this.props.useWebVersion || externalInterfaceStore.isGeorgeApp) &&
                    <TopBackgroundColor />
                }
                <pr.A11yPopupHider>
                    <SigningHeaderBackground />
                    <pr.A11yRefocus
                        focusId={`${ui.isPopupVisible}`} // Hack refocuse when popup is hidden
                    >
                        <BackButton
                            size={30}
                            offset={{ x: 0, y: 10 }}
                            onClick={this.handleClose}
                        />
                    </pr.A11yRefocus>

                    <h1
                        style={{
                            ...pr.STYLE_SEMI_24_WHITE,
                            position: "relative",
                            marginTop: 60
                        }}
                    >
                        <i18n.FormattedMessage id="signing.title" />
                    </h1>

                    {/* {
                        this.isLoading &&
                        <div role="alert">Bitte warten
                        </div>
                    } */}
                    {/* <div aria-live="polite"> */}

                    {this.renderOverlayBox()}
                    {this.renderFlicker()}
                    {this.signingStore.signingMethod === "TAC" ? this.renderResendTAC() : null}
                </pr.A11yPopupHider>

                <ChoosePhoneDialog
                    show={this.signingStore.signingState === "CHOOSE_PHONE"}
                    isLoading={this.isLoading}
                    phoneOptions={this.signingStore.phoneOptions}
                    onOption={(phoneOption: IPhoneOption) => this.signingStore.postSign(phoneOption)}
                    onCancel={this.handleClose}
                />
            </SigningComponentWrapper >
        );
    }
}




// WEBPACK FOOTER //
// ./src/components/signing/views/SigningComponent.tsx